<template>
    <div class="section-container" :class="{'active': isDoingConfiguration, 'has-visibility-rules': hasVisibilityRules}">
        <!-- Conditional visibility indicator badge for section -->
        <div v-if="hasVisibilityRules" class="section-visibility-indicator" :title="visibilityTooltip">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <span class="condition-dot">?</span>
            <span class="indicator-label">Conditional</span>
        </div>

        <!-- Break point badge: patients press (Save &) Continue after this section. Anchored
             top-right so it never collides with the left-anchored Conditional badge. -->
        <div
            v-if="section.breakAfter"
            class="section-break-indicator"
            data-test="section-break-indicator"
            title="Break point: patients press Save & Continue after this section and move to the next part. Ignored while this section is hidden by a visibility rule."
        >Break point</div>

        <SectionNavigationBar
            :section="section"
            :permissions="permissions"
            @active="setActive"
        />

        <component
            :is="sectionViewComponent"
            :section="section"
            :rows="rows"
            :controls="controls"
            :key="section.uniqueId"
            :permissions="permissions"
            :siteID="siteID"
            :sections="sections"
        />
    </div>
</template>

<script>
    import {SECTION_TYPES} from "../../configs/section";
    import SectionNavigationBar from "../../views/builder/SectionNavigationBar";

    export default {
        name: "SectionContainer",
        components: {SectionNavigationBar},
        props: {
            section: Object,
            rows: Object,
            controls: Object,
            permissions: Object,
            siteID: String,
            sections: {
                type: Object,
                default: () => ({})
            }
        },
        data: () => ({
            isDoingConfiguration: false
        }),
        methods: {
            /**
             * Set Active in order to show the holder of current editing section
             * @param val
             */
            setActive(val = true) {
                this.isDoingConfiguration = val
            },

            /**
             * Format operator for display in visibility tooltip
             */
            formatOperator(op) {
                const opMap = {
                    'equals': '=',
                    'notEquals': '≠',
                    'in': 'in',
                    'notIn': 'not in',
                    'lt': '<',
                    'lte': '≤',
                    'gt': '>',
                    'gte': '≥'
                }
                return opMap[op] || op || '='
            },

            /**
             * Get human-readable field label from field ID/name, including section name for context
             */
            getFieldLabel(fieldId) {
                if (!fieldId) return '?'

                // Search through controls to find matching field
                if (this.controls && typeof this.controls === 'object') {
                    for (const ctrlId in this.controls) {
                        const ctrl = this.controls[ctrlId]
                        // Match by name or uniqueId
                        if (ctrl && (ctrl.name === fieldId || ctrl.uniqueId === fieldId || ctrlId === fieldId)) {
                            const fieldLabel = ctrl.label || ctrl.name || fieldId
                            // Find which section this control belongs to
                            const sectionName = this.findSectionForControl(ctrlId)
                            if (sectionName) {
                                return `${sectionName} > ${fieldLabel}`
                            }
                            return fieldLabel
                        }
                    }
                }

                // If not found, return the original fieldId (might already be a name)
                return fieldId
            },

            /**
             * Find the section name that contains a given control
             */
            findSectionForControl(controlId) {
                if (!this.sections || typeof this.sections !== 'object') return ''

                for (const sectionId in this.sections) {
                    const section = this.sections[sectionId]
                    if (section && section.controls && Array.isArray(section.controls)) {
                        if (section.controls.includes(controlId)) {
                            return section.headline || section.name || ''
                        }
                    }
                }
                return ''
            }
        },

        computed: {
            sectionViewComponent() {
                return SECTION_TYPES[this.section.type].builderView
            },

            /**
             * Check if this section has visibility rules configured
             */
            hasVisibilityRules() {
                const rules = this.section && this.section.visibilityRules
                if (!rules || !Array.isArray(rules) || rules.length === 0) return false
                // Check if there are actual conditions defined
                const firstRule = rules[0]
                return firstRule && firstRule.conditions && Array.isArray(firstRule.conditions) && firstRule.conditions.length > 0
            },

            /**
             * Generate tooltip text describing the visibility rules with actual conditions
             */
            visibilityTooltip() {
                if (!this.hasVisibilityRules) return ''
                const rules = this.section.visibilityRules[0]
                const joiner = rules.joiner || 'OR'
                const conditions = rules.conditions || []

                // Build human-readable condition descriptions
                const conditionTexts = conditions.map(cond => {
                    const fieldId = cond.field || '?'
                    // Look up the actual field label/name from controls
                    const fieldLabel = this.getFieldLabel(fieldId)
                    const op = this.formatOperator(cond.op)
                    const value = cond.value !== undefined ? `"${cond.value}"` : '?'
                    return `${fieldLabel} ${op} ${value}`
                })

                const rulesText = conditionTexts.join(` ${joiner} `)
                return `Show when: ${rulesText}`
            }
        },
    }
</script>

<style scoped>
</style>